// client/src/components/StudentsTab.jsx
// "תלמידים" tab on CampDetailPage. Filterable list + create/edit/remove
// modals. Pagination is simple offset-based (matches the wave-2 service).
//
// Layout:
//   - Top: 3-tile KPI strip (total / with-card / classes-on-page).
//   - Toolbar: search with × clear button, class chip-pills auto-derived
//     from the current page, with/without-card filter chips, import + new.
//   - List: airy rows with neutral initials avatars (one calm tile for every
//     student — no per-student colour).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAllStudents,
  useStudentBalances,
  useCreateStudent,
  useUpdateStudent,
  useDeleteStudent,
  useBulkDeleteStudents,
} from '../hooks/useStudents.js';
import Spinner from './Spinner.jsx';
import Modal from './Modal.jsx';
import StudentForm from './StudentForm.jsx';
import StudentsImportModal from './StudentsImportModal.jsx';
import PointsImportModal from './PointsImportModal.jsx';
import StudentDetailModal from './StudentDetailModal.jsx';
import CreditPointsModal from './CreditPointsModal.jsx';
import { SortControl } from './ListToolbar.jsx';
import { matchesText, sortRows } from '../lib/listTools.js';
import {
  ArrowDownTrayIcon,
  AcademicCapIcon,
  CreditCardIcon,
  ArchiveBoxXMarkIcon,
  ChevronStartIcon,
  TableCellsIcon,
  BanknotesIcon,
} from './Icons.jsx';
import { formatNumber } from '../lib/currency.js';

/** Build the export href with the active filters as a query string. */
function exportHref(campId, filters) {
  const qs = new URLSearchParams();
  if (filters.className) qs.set('className', filters.className);
  if (filters.q) qs.set('q', filters.q.trim());
  if (filters.hasCard) qs.set('hasCard', filters.hasCard);
  const s = qs.toString();
  return `/api/camps/${campId}/students/export.xlsx${s ? `?${s}` : ''}`;
}

const NO_FILTERS = { className: '', q: '', hasCard: '' };

/** Programmatic file download — same effect as clicking an <a download>. */
function triggerDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const PAGE_SIZE = 50;

// Sort columns. 'default' keeps the server order (class → last → first name).
const SORT_OPTIONS = [
  { key: 'default', label: 'כיתה ושם' },
  { key: 'lastName', label: 'שם משפחה' },
  { key: 'firstName', label: 'שם פרטי' },
  { key: 'className', label: 'כיתה' },
  { key: 'balance', label: 'נקודות' },
  { key: 'hasCard', label: 'כרטיס' },
  { key: 'createdAt', label: 'תאריך הוספה' },
];

export default function StudentsTab({ campId }) {
  const [filters, setFilters] = useState({ className: '', q: '', hasCard: '' });
  const [sort, setSort] = useState({ key: 'default', dir: 'asc' });
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingPoints, setImportingPoints] = useState(false);
  const [editing, setEditing] = useState(null); // student object
  const [editDirty, setEditDirty] = useState(false);
  const [removing, setRemoving] = useState(null); // {id, name}
  const [viewing, setViewing] = useState(null);    // student object (detail modal)
  const [selected, setSelected] = useState(() => new Set()); // bulk-select ids
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [crediting, setCrediting] = useState(null); // { ids, label, bulk }

  // Pull the WHOLE roster once and do all filtering/search/pagination on the
  // client. This makes the class chips, KPIs and search reflect every
  // student rather than just one server page.
  const studentsQ = useAllStudents(campId);
  // Loaded separately + in parallel so it never delays the roster render.
  const balancesQ = useStudentBalances(campId);
  const createMutation = useCreateStudent(campId);
  const updateMutation = useUpdateStudent(campId);
  const deleteMutation = useDeleteStudent(campId);
  const bulkDeleteMutation = useBulkDeleteStudents(campId);

  const all = studentsQ.data?.students ?? [];
  const balances = balancesQ.data; // { [studentId]: points } | undefined while loading
  const total = all.length;

  // Exact KPIs over the full roster (not "as seen on this page").
  const withCard = useMemo(() => all.filter((s) => !!s.cardNumber).length, [all]);
  const allClasses = useMemo(
    () =>
      [...new Set(all.map((s) => s.className).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'he')
      ),
    [all]
  );

  // Client-side filtering — className exact, hasCard tri-state, free-text q
  // over name / card / ids / notes — then the chosen sort (balance sorts need
  // the balances map, which is why sorting lives here and not in the hook).
  const filtered = useMemo(() => {
    const base = all.filter((s) => {
      if (filters.className && s.className !== filters.className) return false;
      if (filters.hasCard === 'true' && !s.cardNumber) return false;
      if (filters.hasCard === 'false' && s.cardNumber) return false;
      return matchesText(s, filters.q, [
        (r) => `${r.firstName} ${r.lastName}`,
        'cardNumber', 'externalId', 'nationalId', 'className', 'notes',
      ]);
    });
    if (sort.key === 'default') return sort.dir === 'asc' ? base : [...base].reverse();
    return sortRows(base, sort, {
      lastName: (r) => `${r.lastName} ${r.firstName}`,
      firstName: (r) => `${r.firstName} ${r.lastName}`,
      className: (r) => r.className,
      balance: (r) => (balances ? balances[r.id] || 0 : 0),
      hasCard: (r) => !!r.cardNumber,
      createdAt: (r) => (r.createdAt ? new Date(r.createdAt) : null),
    });
  }, [all, filters, sort, balances]);

  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasMore = (page + 1) * PAGE_SIZE < filtered.length;

  // ── Bulk selection. "Select all" spans the whole FILTERED set (across
  // pages), not just the current page, so a manager can wipe a class — or
  // everyone — in one action. Selection is kept as a Set of ids and survives
  // pagination; deleting clears it.
  const filteredIds = useMemo(() => filtered.map((s) => s.id), [filtered]);
  const selectedCount = selected.size;
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someFilteredSelected =
    !allFilteredSelected && filteredIds.some((id) => selected.has(id));

  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAllFiltered() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  }
  const clearSelection = () => setSelected(new Set());

  async function confirmBulkDelete() {
    await bulkDeleteMutation.mutateAsync([...selected]);
    clearSelection();
    setBulkConfirm(false);
  }

  return (
    <div className="space-y-4">
      <KpiStrip total={total} withCard={withCard} classes={allClasses.length} />

      <Toolbar
        campId={campId}
        filters={filters}
        onFiltersChange={(next) => { setFilters(next); setPage(0); }}
        sort={sort}
        onSortChange={(next) => { setSort(next); setPage(0); }}
        classes={allClasses}
        totalCount={total}
        filteredCount={filtered.length}
        onImport={() => setImporting(true)}
        onImportPoints={() => setImportingPoints(true)}
        onCreate={() => setCreating(true)}
      />

      {studentsQ.isLoading && !studentsQ.data ? (
        <Spinner label="טוען תלמידים..." />
      ) : studentsQ.isError ? (
        <div className="card-danger p-4 text-joy-900 text-sm">
          שגיאה: {studentsQ.error.message}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={() => setCreating(true)} hasFilters={hasFilters(filters)} />
      ) : (
        <>
          {selectedCount > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-joy-200 bg-joy-50 px-4 py-2.5 animate-slide-up">
              <span className="text-sm font-semibold text-ink-800">
                <strong className="num">{selectedCount}</strong> נבחרו
              </span>
              <div className="flex items-center gap-2">
                <button type="button" className="btn-ghost btn-sm" onClick={clearSelection}>
                  נקה בחירה
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={() =>
                    setCrediting({ ids: [...selected], label: `${selectedCount} תלמידים`, bulk: true })
                  }
                >
                  <BanknotesIcon className="w-4 h-4" />
                  עדכן נקודות
                </button>
                <button
                  type="button"
                  className="btn-danger btn-sm"
                  onClick={() => setBulkConfirm(true)}
                >
                  <ArchiveBoxXMarkIcon className="w-4 h-4" />
                  מחק מסומנים
                </button>
              </div>
            </div>
          )}
          <StudentList
            students={pageItems}
            balances={balances}
            selectedIds={selected}
            onToggleOne={toggleOne}
            allSelected={allFilteredSelected}
            someSelected={someFilteredSelected}
            onToggleAll={toggleAllFiltered}
            filteredCount={filtered.length}
            onOpen={setViewing}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered.length}
            hasMore={hasMore}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}

      <StudentDetailModal
        student={viewing}
        campId={campId}
        onClose={() => setViewing(null)}
        onEdit={() => { const s = viewing; setViewing(null); setEditing(s); }}
        onArchive={() => {
          const s = viewing;
          setViewing(null);
          setRemoving({ id: s.id, name: `${s.firstName} ${s.lastName}` });
        }}
      />

      <CreditPointsModal
        open={!!crediting}
        campId={campId}
        studentIds={crediting?.ids ?? []}
        label={crediting?.label}
        onClose={(ok) => {
          if (ok && crediting?.bulk) clearSelection();
          setCrediting(null);
        }}
      />

      <StudentsImportModal
        open={importing}
        onClose={() => setImporting(false)}
        campId={campId}
      />

      <PointsImportModal
        open={importingPoints}
        onClose={() => setImportingPoints(false)}
        campId={campId}
      />

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        isDirty={createDirty}
        title="תלמיד חדש"
        maxWidth="max-w-xl"
      >
        <StudentForm
          campId={campId}
          submitLabel="צור תלמיד"
          onDirtyChange={setCreateDirty}
          onCancel={() => setCreating(false)}
          onSubmit={async (body) => {
            await createMutation.mutateAsync(body);
            setCreating(false);
          }}
        />
      </Modal>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        isDirty={editDirty}
        title="עריכת תלמיד"
        maxWidth="max-w-xl"
      >
        {editing && (
          <StudentForm
            campId={campId}
            initial={editing}
            submitLabel="שמור שינויים"
            onDirtyChange={setEditDirty}
            onCancel={() => setEditing(null)}
            onSubmit={async (patch) => {
              await updateMutation.mutateAsync({ studentId: editing.id, patch });
              setEditing(null);
            }}
          />
        )}
      </Modal>

      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="ארכוב תלמיד"
      >
        {removing && (
          <>
            <p className="text-sm text-ink-700">
              להעביר את <strong>{removing.name}</strong> לארכיון? התלמיד ייעלם
              מהרשימה אבל היסטוריית העסקאות תישמר.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setRemoving(null)}>
                ביטול
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={deleteMutation.isPending}
                onClick={async () => {
                  await deleteMutation.mutateAsync(removing.id);
                  setRemoving(null);
                }}
              >
                {deleteMutation.isPending ? 'מארכב...' : 'כן, ארכב'}
              </button>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={bulkConfirm}
        onClose={() => setBulkConfirm(false)}
        title="ארכוב תלמידים"
      >
        <p className="text-sm text-ink-700">
          להעביר לארכיון <strong className="num">{selectedCount}</strong> תלמידים
          שנבחרו? הם ייעלמו מהרשימה אבל היסטוריית העסקאות תישמר. ניתן לשחזר רק
          על ידי DBA.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => setBulkConfirm(false)}>
            ביטול
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={bulkDeleteMutation.isPending}
            onClick={confirmBulkDelete}
          >
            {bulkDeleteMutation.isPending ? 'מארכב...' : `כן, ארכב ${selectedCount}`}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function hasFilters(f) {
  return !!(f.className || f.q || f.hasCard);
}

/* ───── KPI strip ────────────────────────────────────────────── */

function KpiStrip({ total, withCard, classes }) {
  // "% with card" is now computed against the WHOLE roster (we fetch all
  // students), so it's an exact figure rather than a per-page estimate.
  const cardPct = total > 0 ? Math.round((withCard / total) * 100) : null;
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="kpi relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 bg-brand-gradient h-1.5" />
        <div className="kpi-label">סה״כ תלמידים</div>
        <div className="kpi-value">{total.toLocaleString('he-IL')}</div>
        <div className="kpi-hint">בכל התכנית</div>
      </div>
      <div className="kpi relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 strap-earn" />
        <div className="kpi-label">עם כרטיס</div>
        <div className="kpi-value">{cardPct != null ? `${cardPct}%` : '—'}</div>
        <div className="kpi-hint num">
          {withCard} מתוך {total}
        </div>
      </div>
      <div className="kpi relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 bg-gold-gradient h-1.5" />
        <div className="kpi-label">כיתות</div>
        <div className="kpi-value">{classes.toLocaleString('he-IL')}</div>
        <div className="kpi-hint">שונות</div>
      </div>
    </div>
  );
}

/* ───── Toolbar ──────────────────────────────────────────────── */

function Toolbar({ campId, filters, onFiltersChange, sort, onSortChange, classes, totalCount, filteredCount, onImport, onImportPoints, onCreate }) {
  const hasAny = hasFilters(filters);
  // When a filter is hiding some students, ask whether to export the full
  // roster or just the filtered subset. With no filter (or a filter that
  // still matches everyone) we download straight away.
  const [askExport, setAskExport] = useState(false);
  const partialFilter = hasAny && filteredCount < totalCount;

  function handleExportClick() {
    if (partialFilter) {
      setAskExport(true);
    } else {
      triggerDownload(exportHref(campId, NO_FILTERS));
    }
  }

  return (
    <>
    <div className="card p-3 sm:p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <SearchIcon className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-ink-400" />
          <input
            type="text"
            aria-label="חיפוש לפי שם, כרטיס או מזהה"
            placeholder="שם, מספר כרטיס, ת.ז., מזהה..."
            className="input ps-9 pe-9"
            value={filters.q}
            onChange={(e) => onFiltersChange({ ...filters, q: e.target.value })}
          />
          {filters.q && (
            <button
              type="button"
              onClick={() => onFiltersChange({ ...filters, q: '' })}
              className="absolute top-1/2 -translate-y-1/2 end-2 w-6 h-6 grid place-items-center rounded-full text-ink-400 hover:text-ink-900 hover:bg-ink-100"
              aria-label="נקה חיפוש"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex gap-1.5">
          <CardChip active={filters.hasCard === ''}      label="כל הכרטיסים" onClick={() => onFiltersChange({ ...filters, hasCard: '' })} />
          <CardChip active={filters.hasCard === 'true'}  label="עם כרטיס"    onClick={() => onFiltersChange({ ...filters, hasCard: 'true' })}  tone="mint" />
          <CardChip active={filters.hasCard === 'false'} label="בלי כרטיס"   onClick={() => onFiltersChange({ ...filters, hasCard: 'false' })} tone="clay" />
        </div>
        <SortControl value={sort} onChange={onSortChange} options={SORT_OPTIONS} />
        <div className="ms-auto flex gap-2 flex-wrap">
          <Link
            to={`/camps/${campId}/order-cards`}
            className="btn-secondary"
          >
            <CreditCardIcon className="w-4 h-4" />
            הזמנת כרטיסים
          </Link>
          <button type="button" className="btn-secondary" onClick={onImport}>
            <ArrowDownTrayIcon className="w-4 h-4" />
            ייבא
          </button>
          <button type="button" className="btn-secondary" onClick={onImportPoints}>
            <BanknotesIcon className="w-4 h-4" />
            טען נקודות
          </button>
          <button type="button" className="btn-secondary" onClick={handleExportClick}>
            <TableCellsIcon className="w-4 h-4" />
            ייצא לאקסל
          </button>
          <button type="button" className="btn-pop" onClick={onCreate}>
            <PlusIcon className="w-4 h-4" />
            תלמיד חדש
          </button>
        </div>
      </div>

      {classes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs font-bold text-ink-500 uppercase tracking-wider me-1">כיתה</span>
          <button
            type="button"
            className={filters.className === '' ? 'chip-active' : 'chip'}
            onClick={() => onFiltersChange({ ...filters, className: '' })}
          >
            הכל
          </button>
          {classes.map((c) => (
            <button
              key={c}
              type="button"
              className={filters.className === c ? 'chip-active' : 'chip'}
              onClick={() => onFiltersChange({ ...filters, className: c })}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {hasAny && (
        <div className="flex justify-end">
          <button
            type="button"
            className="text-xs text-brand-600 hover:text-brand-800 font-semibold"
            onClick={() => onFiltersChange({ className: '', q: '', hasCard: '' })}
          >
            איפוס כל הסינון
          </button>
        </div>
      )}
    </div>

    <Modal
      open={askExport}
      onClose={() => setAskExport(false)}
      title="ייצוא לאקסל"
      maxWidth="max-w-md"
    >
      <p className="text-sm text-ink-700">
        יש סינון פעיל — מוצגים <strong>{filteredCount}</strong> מתוך{' '}
        <strong>{totalCount}</strong> תלמידים. מה לייצא?
      </p>
      <div className="mt-5 flex flex-col sm:flex-row justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={() => setAskExport(false)}>
          ביטול
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => { triggerDownload(exportHref(campId, NO_FILTERS)); setAskExport(false); }}
        >
          כל התלמידים ({totalCount})
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => { triggerDownload(exportHref(campId, filters)); setAskExport(false); }}
        >
          רק המסונן ({filteredCount})
        </button>
      </div>
    </Modal>
    </>
  );
}

function CardChip({ active, label, onClick, tone }) {
  let cls = active ? 'chip-active' : 'chip';
  if (active && tone === 'mint') cls = 'chip bg-mint-600 border-mint-600 text-white hover:bg-mint-700 shadow-sm';
  if (active && tone === 'clay') cls = 'chip bg-clay-600 border-clay-600 text-white hover:bg-clay-700 shadow-sm';
  return (
    <button type="button" onClick={onClick} className={cls}>{label}</button>
  );
}

/* ───── Student list ─────────────────────────────────────────── */

function StudentList({
  students,
  balances,
  selectedIds,
  onToggleOne,
  allSelected,
  someSelected,
  onToggleAll,
  filteredCount,
  onOpen,
}) {
  return (
    <div className="card overflow-hidden">
      {/* Select-all header — spans the whole filtered set, not just this page. */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-ink-100 bg-ink-50/50">
        <TriCheckbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={onToggleAll}
          ariaLabel="בחר את כל המסוננים"
        />
        <span className="text-xs font-semibold text-ink-600">
          בחר הכל ({filteredCount})
        </span>
      </div>
      <ul className="divide-y divide-ink-100">
        {students.map((s) => (
          <StudentRow
            key={s.id}
            student={s}
            balance={balances ? (balances[s.id] || 0) : undefined}
            selected={selectedIds.has(s.id)}
            onToggle={onToggleOne}
            onOpen={onOpen}
          />
        ))}
      </ul>
    </div>
  );
}

// Checkbox that can show the third "indeterminate" state (some-but-not-all
// selected) — only settable via the DOM property, hence the ref.
function TriCheckbox({ checked, indeterminate, onChange, ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      className="w-4 h-4 accent-brand-600 cursor-pointer"
    />
  );
}

function StudentRow({ student: s, balance, selected, onToggle, onOpen }) {
  const initials = ((s.firstName?.[0] || '') + (s.lastName?.[0] || '')).toUpperCase();

  // The whole row is a button — single tap opens the detail modal (where edit /
  // archive live). The select checkbox sits OUTSIDE that button (no nested
  // interactives) and stops propagation so ticking it doesn't also open the
  // detail modal. The points balance sits inline on the inline-end.
  return (
    <li className={`group flex items-stretch transition-colors ${selected ? 'bg-brand-50/60' : ''}`}>
      <label
        className="flex items-center ps-4 pe-1 cursor-pointer shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(s.id)}
          aria-label={`בחר את ${s.firstName} ${s.lastName}`}
          className="w-4 h-4 accent-brand-600 cursor-pointer"
        />
      </label>
      <button
        type="button"
        onClick={() => onOpen(s)}
        className="flex-1 min-w-0 text-start p-4 hover:bg-ink-50/70 transition-colors flex items-center gap-3
                   focus-visible:outline-none focus-visible:bg-brand-50/60"
      >
        <div className="w-11 h-11 rounded-2xl bg-ink-100 text-ink-600 font-bold text-sm grid place-items-center shrink-0">
          {initials || '·'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink-900 truncate">
              {s.firstName} {s.lastName}
            </span>
            {s.className && <span className="badge-indigo">{s.className}</span>}
            {!s.cardNumber && <span className="badge-clay">ללא כרטיס</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-ink-500">
            {s.cardNumber && (
              <span dir="ltr" className="num">כרטיס …{s.cardNumber.slice(-4)}</span>
            )}
            {s.externalId && (
              <span dir="ltr">מזהה: {s.externalId}</span>
            )}
            {s.notes && <span className="truncate max-w-[20rem]">{s.notes}</span>}
          </div>
        </div>

        {/* Points balance — replaces the old edit/archive icons (those actions
            live in the detail modal). Loads in parallel, so it shows a quiet
            placeholder until the balances arrive. */}
        <div className="flex items-center gap-2 shrink-0">
          <PointsPill value={balance} />
          {/* Chevron — visual hint that the row is tappable. RTL flips
              it naturally (points to inline-start = right in RTL). */}
          <ChevronStartIcon className="w-4 h-4 text-ink-300 group-hover:text-brand-500 transition-colors hidden sm:block" />
        </div>
      </button>
    </li>
  );
}

// Per-student points balance pill. `undefined` = still loading. Green for a
// positive balance, neutral for zero, clay for a (rare) negative.
function PointsPill({ value }) {
  if (value === undefined) {
    return <span className="w-12 h-7 rounded-xl bg-ink-100 animate-pulse" aria-hidden />;
  }
  const tone =
    value > 0 ? 'bg-mint-50 text-mint-700 border-mint-200'
    : value < 0 ? 'bg-clay-50 text-clay-700 border-clay-200'
    : 'bg-ink-50 text-ink-500 border-ink-200';
  return (
    <span className={`num inline-flex items-baseline gap-1 px-3 py-1 rounded-xl border font-bold ${tone}`}>
      <span className="text-base">{formatNumber(value)}</span>
      <span className="text-[10px] font-semibold opacity-70">נק׳</span>
    </span>
  );
}

/* ───── Pagination ───────────────────────────────────────────── */

function Pagination({ page, pageSize, total, hasMore, onPrev, onNext }) {
  if (total <= pageSize && page === 0) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  return (
    <div className="flex items-center justify-between text-sm text-ink-600">
      <div className="num">מציג {from}-{to} מתוך {total}</div>
      <div className="flex gap-2">
        <button type="button" className="btn-secondary btn-sm" onClick={onPrev} disabled={page === 0}>
          קודם
        </button>
        <button type="button" className="btn-secondary btn-sm" onClick={onNext} disabled={!hasMore}>
          הבא
        </button>
      </div>
    </div>
  );
}

/* ───── Empty state ──────────────────────────────────────────── */

function EmptyState({ hasFilters, onCreate }) {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-mint-gradient text-white grid place-items-center mb-4 shadow-pop">
        <AcademicCapIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">
        {hasFilters ? 'אין תוצאות לחיפוש' : 'אין תלמידים עדיין'}
      </h2>
      {!hasFilters && (
        <>
          <p className="mt-2 text-muted">
            ניתן להוסיף ידנית או לייבא מקובץ אקסל.
          </p>
          <button type="button" className="btn-pop btn-lg mt-5" onClick={onCreate}>
            <PlusIcon className="w-5 h-5" />
            תלמיד ראשון
          </button>
        </>
      )}
    </div>
  );
}

function SearchIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 17a7 7 0 100-14 7 7 0 000 14z" />
    </svg>
  );
}

function PlusIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}
